import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar, Pie } from 'react-chartjs-2';
import { getBarChartOptions } from '../../constants';

/**
 * Deck Stats sidebar module - displays deck validation and statistics
 */
function DeckStats({ 
  isCollapsed, 
  onToggle, 
  deckValidation,
  getLevelChartData,
  getCharacterChartData,
  chartOptions
}) {
  const { errors, stats } = deckValidation;
  
  // Get level chart data with maxCount for dynamic scaling
  const levelData = useMemo(() => getLevelChartData(), [getLevelChartData]);
  const dynamicBarChartOptions = useMemo(() => getBarChartOptions(levelData.maxCount || 0), [levelData.maxCount]);

  return (
    <div className="sidebar-section deck-validator">
      <div className="section-header">
        <h4>📊 Deck Stats</h4>
        <button 
          className="collapse-btn" 
          onClick={onToggle}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? '▶' : '▼'}
        </button>
      </div>
      
      {!isCollapsed && (
        <div className={`stats-box ${errors.length > 0 ? 'has-errors' : 'valid'}`}>
          <div className="stat-item">
            <span className="stat-label">Total Cards:</span>
            <span className={`stat-value ${stats.totalCards > 50 ? 'error' : ''}`}>
              {stats.totalCards}/50
            </span>
          </div>
          
          {stats.totalCards > 0 && (
            <>
              {/* Level Distribution Bar Chart */}
              <div className="chart-container level-chart">
                <h5>Level Distribution</h5>
                <Bar data={levelData} options={dynamicBarChartOptions} />
              </div>

              {/* Character Distribution Pie Chart */}
              <div className="chart-container character-chart">
                <h5>Character Distribution</h5>
                <Pie data={getCharacterChartData()} options={chartOptions} />
              </div>
            </>
          )}

          {stats.sceneCards > 0 && (
            <div className="stat-item">
              <span className="stat-label">Scene Cards:</span>
              <span className="stat-value">{stats.sceneCards}</span>
            </div>
          )}
        </div>
      )}

      {!isCollapsed && (
        <>
          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="validator-errors">
              <h5>⚠️ Deck Errors:</h5>
              {errors.map((error, idx) => (
                <div key={idx} className="error-message">{error}</div>
              ))}
            </div>
          )}

          {errors.length === 0 && stats.totalCards > 0 && (
            <div className="validator-success">
              ✅ Deck is valid!
            </div>
          )}
        </>
      )}
    </div>
  );
}

DeckStats.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  deckValidation: PropTypes.shape({
    errors: PropTypes.array.isRequired,
    stats: PropTypes.shape({
      totalCards: PropTypes.number.isRequired,
      cardsByLevel: PropTypes.object,
      sceneCards: PropTypes.number,
      characterBreakdown: PropTypes.object,
    }).isRequired,
  }).isRequired,
  getLevelChartData: PropTypes.func.isRequired,
  getCharacterChartData: PropTypes.func.isRequired,
  chartOptions: PropTypes.object.isRequired,
};

export default DeckStats;
